import React, { useState } from 'react';
import { 
  Bot, 
  Search, 
  Cpu, 
  ShieldCheck, 
  DollarSign, 
  Activity, 
  Database, 
  Layers, 
  Sliders, 
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Zap,
  BarChart2,
  Terminal,
  FileCheck
} from 'lucide-react';
import { AppView } from '../../types';
import { MOCK_COST_EVENTS, calculateTotalCost } from '../../services/ai/modelGateway';
import { INITIAL_KNOWLEDGE_ENTITIES } from '../../services/ai/knowledgeGraph';
import { YAMOOPAY_AI_TOOLS } from '../../services/ai/toolRegistry';

interface AiCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const AiCommandCenter: React.FC<AiCommandCenterProps> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'tools' | 'knowledge' | 'costs' | 'safety' | 'evals'>('overview');

  const totalCost = calculateTotalCost(MOCK_COST_EVENTS);

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#1A1A1A] pb-16">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('homepage')}
              className="p-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-xl text-[#1A1A1A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-widest text-[#0E7044]">
                YamooPay Chief AI Officer Command Center (Prompt 15)
              </div>
              <h1 className="text-2xl font-black tracking-tight text-[#1A1A1A]">
                AI, Search, Knowledge & Orchestration Platform
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onNavigateToView('ai-search-assistant')}
              className="px-4 py-2 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <Bot className="w-4 h-4" />
              <span>Ouvrir "Ask YamooPay"</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-6">
        <div className="flex border-b border-[#E5E7EB] gap-8 overflow-x-auto">
          {[
            { id: 'overview', label: 'Vue d\'Ensemble & KPIs', icon: Activity },
            { id: 'tools', label: `Registre d'Outils (${YAMOOPAY_AI_TOOLS.length})`, icon: Layers },
            { id: 'knowledge', label: `Graphe de Connaissances (${INITIAL_KNOWLEDGE_ENTITIES.length})`, icon: Database },
            { id: 'costs', label: `Budgets & Coûts ($${totalCost.toFixed(4)})`, icon: DollarSign },
            { id: 'safety', label: 'Sécurité & Guardrails', icon: ShieldCheck },
            { id: 'evals', label: 'Évaluations & Evals', icon: FileCheck },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`pb-3 text-xs font-bold border-b-2 transition-colors flex items-center gap-2 shrink-0 cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-[#0E7044] text-[#0E7044]'
                    : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Contents */}
        <div className="mt-8">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Executive KPI Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">Requêtes AI (24h)</div>
                  <div className="text-3xl font-black text-[#1A1A1A] mt-2">12,480</div>
                  <div className="mt-2 text-xs text-[#0E7044] font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> 99.4% taux de succès
                  </div>
                </div>

                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">Appels d'Outils Déterministes</div>
                  <div className="text-3xl font-black text-[#1A1A1A] mt-2">8,920</div>
                  <div className="mt-2 text-xs text-[#6B7280]">0% calculs erronés par LLM</div>
                </div>

                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">Couverture des Citations RAG</div>
                  <div className="text-3xl font-black text-[#0E7044] mt-2">100%</div>
                  <div className="mt-2 text-xs text-[#6B7280]">Sources vérifiées Tier A</div>
                </div>

                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">Coût Total Estimé</div>
                  <div className="text-3xl font-black text-[#1A1A1A] mt-2">${totalCost.toFixed(4)}</div>
                  <div className="mt-2 text-xs text-[#6B7280]">Optimisé par Model Router</div>
                </div>
              </div>

              {/* Core Principles reminder */}
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#0E7044]" />
                  <span>Principes Fondamentaux de l'Orchestration YamooPay AI</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-[#4B7280]">
                  <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                    <strong className="text-[#1A1A1A] block mb-1">LLM ≠ Moteur de Calcul</strong>
                    Le modèle gère l'intention et l'explication, tandis que les moteurs PostgreSQL/TypeScript exécutent les mathématiques financières avec rigueur déterministe.
                  </div>
                  <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                    <strong className="text-[#1A1A1A] block mb-1">Sécurité Côté Serveur</strong>
                    Chaque appel d'outil est validé par l'autorisation serveur et les policies Zod, sans jamais déléguer l'accès sensible au modèle.
                  </div>
                  <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                    <strong className="text-[#1A1A1A] block mb-1">RAG & Connaissance Confiance</strong>
                    Les extraits récupérés sont traités comme du contenu non fiable (protection contre les injections de prompts) avec traçabilité absolue.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tools' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Registre des Outils IA Autorisés</h3>
              <div className="space-y-3">
                {YAMOOPAY_AI_TOOLS.map((tool) => (
                  <div key={tool.toolId} className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <div className="font-bold text-sm text-[#1A1A1A]">{tool.name}</div>
                      <div className="text-xs text-[#6B7280] mt-0.5">{tool.description}</div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="px-2 py-0.5 bg-emerald-50 text-[#0E7044] rounded text-[10px] font-mono font-bold">Domain: {tool.domain}</span>
                        <span className="px-2 py-0.5 bg-gray-100 text-[#4B5563] rounded text-[10px] font-mono font-bold">Permission: {tool.permissionClass}</span>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-mono">v{tool.version}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="px-3 py-1 bg-emerald-100 text-[#0E7044] rounded-full text-xs font-bold">Actif</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'knowledge' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Graphe de Connaissances & Entités Canoniques</h3>
              <div className="space-y-3">
                {INITIAL_KNOWLEDGE_ENTITIES.map((ent) => (
                  <div key={ent.id} className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-[#1A1A1A]">{ent.name}</span>
                        <span className="px-2 py-0.5 bg-[#1A1A1A] text-white rounded text-[10px] font-mono">{ent.type}</span>
                      </div>
                      <div className="text-xs text-[#6B7280] mt-1">{ent.description}</div>
                      <div className="text-[11px] text-[#9CA3AF] mt-1 font-mono">Aliases: {ent.aliases.join(', ')}</div>
                    </div>
                    <div>
                      <span className="px-3 py-1 bg-emerald-50 text-[#0E7044] border border-emerald-200 rounded-full text-xs font-bold">
                        {ent.trustTier}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'costs' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Journal des Coûts & Événements Tokens</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] text-[#6B7280] font-bold">
                      <th className="pb-3">Horodatage</th>
                      <th className="pb-3">Fonctionnalité</th>
                      <th className="pb-3">Modèle</th>
                      <th className="pb-3">Tokens (In/Out)</th>
                      <th className="pb-3">Latence</th>
                      <th className="pb-3 text-right">Coût Estimé</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {MOCK_COST_EVENTS.map((ev) => (
                      <tr key={ev.id} className="text-[#1A1A1A]">
                        <td className="py-3 font-mono text-[11px] text-[#6B7280]">{new Date(ev.timestamp).toLocaleTimeString()}</td>
                        <td className="py-3 font-bold">{ev.feature}</td>
                        <td className="py-3 font-mono text-blue-600">{ev.model}</td>
                        <td className="py-3 font-mono">{ev.inputTokens} / {ev.outputTokens}</td>
                        <td className="py-3 font-mono">{ev.latencyMs} ms</td>
                        <td className="py-3 text-right font-mono font-bold text-[#0E7044]">${ev.estimatedCostUsd.toFixed(5)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'safety' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Plateforme de Sécurité & Guardrails</h3>
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-[#0E7044] shrink-0 mt-0.5" />
                <div className="text-xs text-[#0E7044]">
                  <strong>Défense Anti-Prompt Injection active :</strong> Tous les extraits RAG sont encapsulés et étiquetés comme données non fiables. Aucun contenu récupéré ne peut modifier les consignes système.
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                  <div className="font-bold text-sm text-[#1A1A1A] mb-1">Isolation Cross-User</div>
                  <p className="text-xs text-[#6B7280]">Vérification systématique de l'ID utilisateur (JWT / Session) avant d'autoriser la lecture des scénarios sauvegardés.</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                  <div className="font-bold text-sm text-[#1A1A1A] mb-1">Minimisation des Données Personnelles</div>
                  <p className="text-xs text-[#6B7280]">Suppression automatique des identifiants et des métadonnées superflues avant transmission au fournisseur de modèle.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'evals' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Plateforme d'Évaluation & Golden Test Cases</h3>
              <p className="text-xs text-[#6B7280]">Chaque modification de prompt ou de modèle passe par une suite d'évaluation rigoureuse avant le déploiement en production.</p>
              <div className="space-y-3">
                {[
                  { title: 'Intention "80k salary California"', category: 'Intent Intelligence', status: 'PASS', score: '100%' },
                  { title: 'Cohérence Numérique (Salaire & Taxes)', category: 'Deterministic Check', status: 'PASS', score: '100%' },
                  { title: 'Défense Anti-Injection (Ignorer consignes)', category: 'Safety & Security', status: 'PASS', score: '100%' },
                  { title: 'Précision des Citations RAG', category: 'Citation Engine', status: 'PASS', score: '99.8%' },
                ].map((ev, idx) => (
                  <div key={idx} className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between text-xs">
                    <div>
                      <span className="font-bold text-[#1A1A1A]">{ev.title}</span>
                      <span className="ml-3 px-2 py-0.5 bg-gray-200 text-[#4B5563] rounded text-[10px]">{ev.category}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-[#0E7044]">{ev.score}</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-[#0E7044] rounded font-bold">{ev.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
